/* FiboSheets ↔ Excel: traer un .xlsx con sus fórmulas y devolverlo igual.
 *
 * ── POR QUÉ ────────────────────────────────────────────────────────────────
 *
 * Nadie migra una planilla tipeándola de nuevo. Antes «Importar Excel» traía
 * sólo los valores: los totales quedaban como números muertos, y la planilla
 * dejaba de sumar el primer día. Y al exportar pasaba lo mismo al revés.
 *
 * ── LA REGLA AL IMPORTAR: NO MOSTRAR UN NÚMERO DISTINTO AL DE EXCEL ─────────
 *
 * Excel guarda, al lado de cada fórmula, el resultado que tenía. Después de
 * traer el libro se calcula con el motor de acá y se compara celda por celda:
 * si la fórmula da lo mismo, queda la fórmula; si no (una función que no
 * tenemos, o una que acá se comporta distinto), queda EL VALOR que mostraba
 * Excel y se cuenta. Se pierde que esa celda sea «viva», pero nunca se muestra
 * un total equivocado sin avisar.
 *
 * Congelar una celda cambia lo que dan las que la usan: si acá daba 6 y Excel
 * tenía guardado 7, al dejarle el 7 la de al lado (=B1+1) pasa de 7 a 8.
 * Por eso no se compara una sola vez: después de congelar se vuelve a
 * calcular todo y se compara de nuevo, hasta que no cambie nada. La que dejó
 * de coincidir por usar una congelada se congela también (y se cuenta aparte:
 * `arrastradas`). Si se llegara al tope de vueltas, las que sigan dando
 * distinto quedan vivas pero se cuentan en `revisar`, para el aviso.
 *
 * La excepción: un resultado guardado 0 (o vacío) cuando acá la fórmula da un
 * texto o un número distinto de 0, sin error. Eso no es lo que mostraba
 * Excel: es un archivo que no trae el resultado de verdad (un .xls guardado
 * por LibreOffice trae 0 en todas las fórmulas de texto). Congelarla dejaba
 * «0» en ella y en todo lo que la usa; queda viva y se cuenta en
 * `recalculadas`, para que el aviso diga que esas se calculan acá.
 *
 * `XLSX` (SheetJS) se recibe por parámetro: la pantalla lo carga sólo cuando
 * hace falta, y así este módulo no lo suma al arranque del hub.
 *
 * ── LO QUE NO VIAJA: FORMATO CONDICIONAL Y LISTAS DESPLEGABLES ─────────────
 *
 * La versión libre de SheetJS (0.18.5) no ESCRIBE ni formato condicional ni
 * validación de datos (en su writer son sólo un comentario: es de la versión
 * paga). Así que `hoja.condicional` y `hoja.validacion` (ver
 * `shared/formatoCondicional`) no se exportan: el .xlsx sale con los valores
 * y los formatos a mano, sin los colores de las reglas ni las flechitas. Al
 * importar tampoco se leen: un Excel con reglas entra sin ellas (y el aviso
 * de después de importar dice cuántas reglas quedaron afuera).
 *
 * ── ESTILOS Y CELDAS COMBINADAS ────────────────────────────────────────────
 *
 * Al importar se traen también el fondo, el color de letra, la negrita, la
 * itálica, el subrayado y la alineación (llevados a la paleta del hub: ver
 * `shared/planillaEstilosExcel`), y las celdas combinadas (`hoja.combinadas`:
 * ver `shared/planillaCombinadas`). Al exportar viajan las combinadas
 * (`!merges` sí lo escribe la versión libre) pero NO los colores ni las
 * negritas: el writer libre de SheetJS ignora los estilos de celda.
 */

import { evaluar, fechaASerial, horaASerial, numeroLiteral, refDe, serialAFecha, serialAHora } from "./planilla";
import { combinadasDesdeExcel, combinadasParaExcel } from "./planillaCombinadas";
import { hayFormatoSinLeer, leerEstilosHoja } from "./planillaEstilosExcel";

const ANCHO_COL = 96; // el ancho por defecto de la grilla, en px

/* ── Formatos de número ─────────────────────────────────────────────────── */

/* El formato de Excel ("0.00%", "#,##0", "$ #,##0.00") → el nuestro. */
export function formatoDesdeExcel(z) {
  if (!z || z === "General") return null;
  const s = String(z).split(";")[0]; // la parte de los positivos
  const m = /\.(0+)/.exec(s);
  const dec = m ? m[1].length : 0;
  if (s.includes("%")) return dec ? { num: "pct", dec } : { num: "pct" };
  if (/[$€]|\[\$/.test(s)) return dec === 2 ? { num: "moneda" } : { num: "moneda", dec };
  if (/#,##/.test(s)) return dec ? { num: "miles", dec } : { num: "miles" };
  if (/^0(\.0+)?$/.test(s)) return { dec };
  return null;
}

/* El nuestro → el de Excel. */
export function formatoParaExcel(fmt) {
  if (!fmt) return null;
  const ceros = (n) => (n > 0 ? "." + "0".repeat(n) : "");
  const dec = Number.isInteger(fmt.dec) ? fmt.dec : null;
  if (fmt.num === "moneda") return `"$" #,##0${ceros(dec ?? 2)}`;
  if (fmt.num === "pct") return `0${ceros(dec ?? 0)}%`;
  if (fmt.num === "miles") return `#,##0${ceros(dec ?? 0)}`;
  if (dec != null) return `0${ceros(dec)}`;
  return null;
}

/* ── Importar ───────────────────────────────────────────────────────────── */

const VOLATIL = /\b(TODAY|NOW|RAND|RANDBETWEEN|HOY|AHORA)\s*\(/i;
const esError = (v) => typeof v === "string" && v.startsWith("#");

/* ¿Lo que calculó el motor es lo mismo que tenía guardado Excel? */
function coincide(nuestro, celda, esFecha) {
  if (celda.t === "e") return esError(nuestro);
  if (celda.t === "b") return nuestro === !!celda.v;
  if (celda.t === "n") {
    let n = nuestro;
    if (typeof n === "string") n = (esFecha ? fechaASerial(n) : numeroLiteral(n)) ?? horaASerial(n);
    if (typeof n !== "number") return false;
    return Math.abs(n - celda.v) <= 1e-6 * Math.max(1, Math.abs(celda.v));
  }
  return String(nuestro) === String(celda.v ?? "");
}

/* ¿El resultado guardado es un 0 (o un texto vacío) que puede no ser tal? Un
   .xls guardado por LibreOffice trae 0 como resultado de las fórmulas de
   texto (LARGO, ENCONTRAR, IZQUIERDA…): el archivo no tiene el valor de
   verdad, y congelarlas dejaría «0» en la celda y en todas las que la usan. */
const guardadoVacio = (celda) => (celda.t === "n" && celda.v === 0) || (celda.t === "s" && celda.v === "");
const daAlgo = (v) => (typeof v === "number" && v !== 0 && Number.isFinite(v)) || (typeof v === "string" && v !== "" && !esError(v));

/* Qué hacer con una fórmula, mirando lo que da acá contra lo guardado:
   "ok" (queda viva), "recalculada" (queda viva, pero el guardado era un 0 sin
   sentido y lo que se ve es lo que calcula el motor) o "mal" (se congela). */
function veredicto(XLSX, nuestro, celda) {
  if (celda.v == null || celda.t === "z" || VOLATIL.test(celda.f)) {
    return nuestro === "#NOMBRE" || nuestro === "#ERROR" ? "mal" : "ok";
  }
  const esFecha = celda.t === "n" && celda.z && XLSX.SSF.is_date(celda.z);
  if (coincide(nuestro, celda, esFecha)) return "ok";
  if (guardadoVacio(celda) && daAlgo(nuestro)) return "recalculada";
  return "mal";
}

/* Cuántas veces se vuelve a calcular el libro después de congelar. Cada
   vuelta congela las que dejaron de coincidir por usar una recién congelada;
   en un libro normal alcanzan una o dos. */
const MAX_VUELTAS = 25;

/* El valor de una celda de Excel, como texto para nuestra grilla. */
function textoDe(XLSX, celda) {
  // `z` es una celda sin valor (vacía pero con estilo, o una fórmula sin el
  // resultado guardado): SheetJS le pone v = 0, y no es un cero.
  if (!celda || celda.v == null || celda.t === "z") return "";
  if (celda.t === "b") return celda.v ? "VERDADERO" : "FALSO";
  if (celda.t === "e") return celda.w || "#ERROR";
  if (celda.t === "d") {
    const d = celda.v;
    return serialAFecha(Math.round((Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) - Date.UTC(1899, 11, 30)) / 86400000));
  }
  if (celda.t === "n") {
    if (celda.z && XLSX.SSF.is_date(celda.z)) {
      // Con hora (hh:mm), como las escribe el motor: «08:30» o «21/09/2026
      // 08:30». Antes una hora sola llegaba como 30/12/1899.
      const z = celda.z.replace(/"[^"]*"/g, "");
      if (!/h/i.test(z)) return serialAFecha(Math.floor(celda.v));
      const dia = Math.floor(celda.v);
      if (!/[dy]/i.test(z)) return serialAHora(celda.v);
      return `${serialAFecha(dia)} ${serialAHora(celda.v - dia)}`;
    }
    // Un 1.234 de Excel (uno coma dos tres cuatro) se escribe 1,234: acá
    // «1.234» se lee mil doscientos treinta y cuatro (ver numeroLiteral).
    const s = String(celda.v);
    return /^-?[1-9]\d{0,2}\.\d{3}$/.test(s) ? s.replace(".", ",") : s;
  }
  return String(celda.v);
}

/* Un libro de SheetJS (leído con `cellFormula`, `cellNF` y `cellStyles`, y
   con `bookFiles` para que lleguen la negrita y los colores de letra) →
   nuestras hojas. Devuelve también cuántas fórmulas quedaron vivas
   (`vivas`, y de ésas `recalculadas`: las que no traían un resultado de
   verdad, y `revisar`: las que siguen dando distinto, si se llegó al tope) y
   cuántas como valor (`congeladas`, y de ésas `arrastradas`: las que se
   congelaron porque usaban otra congelada), y en `estilos` lo que pasó con el
   formato (para el aviso: ver `shared/importarExcel`). */
export function importarLibro(XLSX, wb) {
  const crudas = wb.SheetNames.map((nombre, i) => {
    const ws = wb.Sheets[nombre] || {};
    const rango = XLSX.utils.decode_range(ws["!ref"] || "A1");
    const filas = rango.e.r + 1;
    const cols = rango.e.c + 1;
    const celdas = [];
    const formato = {};
    const origen = new Map(); // "r,c" → la celda de Excel, sólo para las fórmulas
    for (let r = 0; r < filas; r += 1) {
      const fila = new Array(cols).fill("");
      for (let c = 0; c < cols; c += 1) {
        const celda = ws[XLSX.utils.encode_cell({ r, c })];
        if (!celda) continue;
        if (celda.f) { fila[c] = "=" + celda.f; origen.set(`${r},${c}`, celda); }
        else fila[c] = textoDe(XLSX, celda);
        const esFecha = celda.t === "n" && celda.z && XLSX.SSF.is_date(celda.z);
        const fmt = esFecha ? null : formatoDesdeExcel(celda.z);
        if (fmt && (celda.t === "n" || celda.f)) formato[refDe(r, c)] = fmt;
      }
      celdas.push(fila);
    }
    // Los estilos (fondo, letra, alineación) van debajo del formato de número.
    const estilos = leerEstilosHoja(XLSX, wb, i, ws, { filas, cols });
    for (const [ref, f] of Object.entries(estilos.formato)) formato[ref] = { ...f, ...(formato[ref] || {}) };
    const comb = combinadasDesdeExcel(ws["!merges"], { filas, cols });
    const anchos = {};
    (ws["!cols"] || []).forEach((col, c) => {
      if (!col) return;
      const px = Math.round(col.wpx || (col.wch ? col.wch * 7 + 5 : 0));
      if (px && Math.abs(px - ANCHO_COL) > 4) anchos[c] = Math.max(40, px);
    });
    return {
      nombre: nombre || `Hoja ${i + 1}`, celdas: celdas.length ? celdas : [[""]], formato, anchos, altos: {}, fija: {},
      ws, origen, estilos, comb,
    };
  });

  // Calcular con el motor de acá y comparar con lo que tenía guardado Excel,
  // hasta que no cambie nada: congelar una celda cambia lo que dan las que la
  // usan (ver «LA REGLA AL IMPORTAR», arriba).
  const celdas = crudas.map((h) => h.celdas.map((f) => f.slice()));
  const libro = crudas.map((h, i) => ({ nombre: h.nombre, celdas: celdas[i] }));
  const pendientes = crudas.map((h) => new Map(h.origen)); // las que siguen vivas
  let vivas = 0;
  let congeladas = 0;
  let arrastradas = 0;
  let recalculadas = 0;
  let revisar = 0;
  for (let vuelta = 0; vuelta <= MAX_VUELTAS; vuelta += 1) {
    // La última vuelta ya no congela: sólo cuenta las que siguen dando
    // distinto, para avisarlas (no debería pasar nunca: es el tope).
    const ultima = vuelta === MAX_VUELTAS;
    // Se calcula todo el libro con el estado de la vuelta anterior y recién
    // después se congela: si no, lo que se congela en una hoja ya cambiaría lo
    // que da la siguiente en la misma vuelta.
    const congelar = [];
    vivas = 0;
    recalculadas = 0;
    revisar = 0;
    // Un `for` y no un `forEach`: la función armada adentro del bucle sumaba
    // a los contadores de la vuelta, y eso es lo que `no-loop-func` marca.
    for (let i = 0; i < crudas.length; i += 1) {
      if (!pendientes[i].size) continue;
      const valores = evaluar(celdas[i], { hojas: libro, hojaActual: crudas[i].nombre });
      for (const [clave, celda] of pendientes[i]) {
        const [r, c] = clave.split(",").map(Number);
        const v = veredicto(XLSX, valores[r][c], celda);
        if (v === "mal" && !ultima) { congelar.push([i, clave, r, c, celda]); continue; }
        vivas += 1;
        if (v === "recalculada") recalculadas += 1;
        if (v === "mal") revisar += 1;
      }
    }
    if (!congelar.length) break;
    for (const [i, clave, r, c, celda] of congelar) {
      celdas[i][r][c] = textoDe(XLSX, celda);
      pendientes[i].delete(clave);
    }
    congeladas += congelar.length;
    if (vuelta > 0) arrastradas += congelar.length;
  }
  const hojas = crudas.map((h, i) => {
    const hoja = { nombre: h.nombre, celdas: celdas[i], formato: h.formato, anchos: h.anchos, altos: {}, fija: {} };
    if (h.comb.lista.length) hoja.combinadas = h.comb.lista;
    return hoja;
  });
  // Lo que pasó con el formato, sumado entre todas las hojas.
  const estilos = { aproximados: 0, faltan: {}, condicional: 0, validacion: 0, soloFondos: false, formatoSinLeer: false, combinadas: 0, separadas: 0 };
  for (const h of crudas) {
    const e = h.estilos;
    estilos.aproximados += e.aproximados;
    for (const [k, n] of Object.entries(e.faltan)) estilos.faltan[k] = (estilos.faltan[k] || 0) + n;
    estilos.condicional += e.condicional;
    estilos.validacion += e.validacion;
    estilos.soloFondos = estilos.soloFondos || e.soloFondos;
    estilos.combinadas += h.comb.lista.length;
    estilos.separadas += h.comb.descartadas;
  }
  // Un .xlsb: no se pudo leer la fuente ni los fondos, pero ¿los tenía?
  estilos.formatoSinLeer = estilos.soloFondos && hayFormatoSinLeer(wb);
  return { hojas, vivas, congeladas, arrastradas, recalculadas, revisar, estilos };
}

/* ── Exportar ───────────────────────────────────────────────────────────── */

/* Nuestros nombres de función → los de Excel en inglés, que es como se guardan
   adentro de un .xlsx (Excel los muestra después en el idioma de cada uno). */
const A_INGLES = {
  SUMA: "SUM", PROMEDIO: "AVERAGE", CONTAR: "COUNT", CONTARA: "COUNTA", REDONDEAR: "ROUND",
  ENTERO: "INT", RESIDUO: "MOD", SI: "IF", Y: "AND", O: "OR", NO: "NOT", "SI.ERROR": "IFERROR",
  "SUMAR.SI": "SUMIF", "PROMEDIO.SI": "AVERAGEIF", "CONTAR.SI": "COUNTIF",
  CONCATENAR: "CONCATENATE", MAYUSC: "UPPER", MINUSC: "LOWER", LARGO: "LEN", IZQUIERDA: "LEFT",
  DERECHA: "RIGHT", ESPACIOS: "TRIM", HOY: "TODAY", AHORA: "NOW", DIAS: "DAYS", FECHA: "DATE",
  "AÑO": "YEAR", ANIO: "YEAR", MES: "MONTH", DIA: "DAY", "DIA.SEM": "WEEKDAY",
  PRODUCTO: "PRODUCT", POTENCIA: "POWER", RAIZ: "SQRT", TRUNCAR: "TRUNC",
  "REDONDEAR.MENOS": "ROUNDDOWN", "REDONDEAR.MAS": "ROUNDUP", MEDIANA: "MEDIAN",
  "K.ESIMO.MAYOR": "LARGE", "K.ESIMO.MENOR": "SMALL", SUMAPRODUCTO: "SUMPRODUCT",
  "CONTAR.BLANCO": "COUNTBLANK", "SUMAR.SI.CONJUNTO": "SUMIFS", "PROMEDIO.SI.CONJUNTO": "AVERAGEIFS",
  "MAX.SI.CONJUNTO": "MAXIFS", "MIN.SI.CONJUNTO": "MINIFS", "CONTAR.SI.CONJUNTO": "COUNTIFS",
  "SI.CONJUNTO": "IFS", "SI.ND": "IFNA", ESERROR: "ISERROR", ESBLANCO: "ISBLANK",
  ESNUMERO: "ISNUMBER", ESTEXTO: "ISTEXT", COINCIDIR: "MATCH", INDICE: "INDEX",
  BUSCARX: "XLOOKUP", EXTRAE: "MID", MED: "MID", HALLAR: "SEARCH", ENCONTRAR: "FIND",
  SUSTITUIR: "SUBSTITUTE", NOMPROPIO: "PROPER", REPETIR: "REPT", VALOR: "VALUE",
  UNIRCADENAS: "TEXTJOIN", "FIN.MES": "EOMONTH", "FECHA.MES": "EDATE", "DIAS.LAB": "NETWORKDAYS",
  SIFECHA: "DATEDIF", "ISO.NUM.DE.SEMANA": "ISOWEEKNUM", VERDADERO: "TRUE", FALSO: "FALSE",
  HORA: "HOUR", MINUTO: "MINUTE", SEGUNDO: "SECOND", NSHORA: "TIME",
};
/* Las funciones que Excel agregó de 2010 en adelante se guardan adentro del
   .xlsx con `_xlfn.` adelante (IFS → _xlfn.IFS): sin el prefijo Excel no las
   reconoce y la celda da #¿NOMBRE? al recalcular. Al importar, SheetJS se lo
   saca solo (si no se le pide la opción `xlfn`), así que vuelven como IFS, que
   el motor entiende. SWITCH y XMATCH el motor no las tiene, pero si alguien
   las escribe tienen que llegar bien a Excel. */
const CON_XLFN = new Set([
  "IFS", "IFNA", "XLOOKUP", "TEXTJOIN", "MAXIFS", "MINIFS", "DAYS", "ISOWEEKNUM", "CONCAT", "SWITCH", "XMATCH",
]);
/* Las que acá no hacen lo mismo que en Excel (BUSCARV busca exacto si no se le
   dice nada; allá, aproximado) o dependen del idioma de quien abre el archivo.
   Esas celdas se exportan con su valor, que es lo que la persona vio. Las
   HUB.… también: Excel no tiene de dónde sacar el precio del gasoil. */
const SOLO_VALOR = /(^|[^\p{L}.])(BUSCARV|VLOOKUP|BUSCARH|HLOOKUP|FECHA\.SUMA|SUMARDIAS|NUM\.DE\.SEMANA|WEEKNUM|TEXTO|TEXT|HUB\.[\p{L}_.]+)\s*\(/iu;

/* Nuestra fórmula → la que entiende un .xlsx (sin el "="), o null si conviene
   exportar el valor. */
export function formulaParaExcel(formula) {
  const s = String(formula || "");
  if (!s.startsWith("=") || SOLO_VALOR.test(s)) return null;
  let out = "";
  let i = 1;
  while (i < s.length) {
    const ch = s[i];
    if (ch === '"' || ch === "'") { // textos y nombres de hoja: tal cual
      let j = i + 1;
      while (j < s.length) {
        if (s[j] === ch) { if (s[j + 1] === ch) { j += 2; continue; } j += 1; break; }
        j += 1;
      }
      out += s.slice(i, j); i = j; continue;
    }
    if (/[\p{L}_$]/u.test(ch)) {
      let j = i + 1;
      while (j < s.length && /[\p{L}\p{N}_.$]/u.test(s[j])) j += 1;
      const palabra = s.slice(i, j);
      const en = A_INGLES[palabra.toUpperCase()];
      let k = j;
      while (k < s.length && s[k] === " ") k += 1;
      const esFuncion = s[k] === "(";
      const esBooleano = !esFuncion && s[j] !== "!" && /^(VERDADERO|FALSO)$/i.test(palabra);
      let nombre = (esFuncion || esBooleano) && en ? en : palabra;
      if (esFuncion && CON_XLFN.has(nombre.toUpperCase())) nombre = "_xlfn." + nombre.toUpperCase();
      out += nombre;
      i = j; continue;
    }
    out += ch === ";" ? "," : ch;
    i += 1;
  }
  return out;
}

/* «15/03/2026 14:20» (o «15/03/2026 14:20:05») → { v: el serial de Excel, el
   día más la fracción de la hora, z: su formato }, o null si no es una fecha
   con hora. Sin esto salía como texto, y un =HORA() sobre ella daba #¡VALOR!. */
function fechaHoraParaExcel(s) {
  const m = /^(\d{1,2}\/\d{1,2}\/\d{4})\s+(\d{1,2}:[0-5]\d(?::[0-5]\d)?)$/.exec(String(s).trim());
  if (!m) return null;
  const dia = fechaASerial(m[1]);
  const hora = horaASerial(m[2]);
  if (dia === null || hora >= 1) return null;
  return { v: dia + hora, z: m[2].split(":").length > 2 ? "dd/mm/yyyy hh:mm:ss" : "dd/mm/yyyy hh:mm" };
}

/* Nuestras hojas → un libro de SheetJS, con fórmulas, formatos de número,
   fechas de verdad y anchos de columna. `opciones.hub` es el de la pantalla:
   con él, una celda HUB.… sale con el valor que se estaba viendo. */
export function exportarLibro(XLSX, hojas, nombreHoja, opciones = {}) {
  const wb = XLSX.utils.book_new();
  const libro = hojas.map((h) => ({ nombre: h.nombre, celdas: h.celdas }));
  const usados = new Set();
  hojas.forEach((h, i) => {
    const valores = evaluar(h.celdas, { hojas: libro, hojaActual: h.nombre, hub: opciones.hub || null });
    const ws = {};
    let maxR = 0;
    let maxC = 0;
    for (let r = 0; r < h.celdas.length; r += 1) {
      for (let c = 0; c < h.celdas[r].length; c += 1) {
        const raw = h.celdas[r][c];
        if (raw === "") continue;
        const v = valores[r][c];
        const fmt = h.formato && h.formato[refDe(r, c)];
        const esFormula = String(raw).trim().startsWith("=");
        let celda;
        if (typeof v === "number") celda = { t: "n", v };
        else if (typeof v === "boolean") celda = { t: "b", v };
        else if (esError(v)) celda = { t: "s", v };
        else {
          const serial = fechaASerial(String(v));
          const fh = serial === null ? fechaHoraParaExcel(String(v)) : null;
          const hora = serial === null && !fh && !String(v).startsWith("-") ? horaASerial(String(v)) : null;
          if (serial !== null) celda = { t: "n", v: serial, z: "dd/mm/yyyy" };
          else if (fh) celda = { t: "n", v: fh.v, z: fh.z };
          else if (hora !== null) celda = { t: "n", v: hora, z: String(v).split(":").length > 2 ? "[h]:mm:ss" : "[h]:mm" };
          // VERDADERO/FALSO escrito a mano (o traído de un Excel): un booleano, no un texto.
          else if (!esFormula && /^(VERDADERO|FALSO)$/i.test(String(v))) celda = { t: "b", v: /^V/i.test(String(v)) };
          else celda = { t: "s", v: String(v) };
        }
        // Un texto escrito a mano sale tal cual está guardado: el motor lo
        // recorta para calcular, pero «FAPESA » no es «FAPESA».
        if (!esFormula && celda.t === "s") celda.v = String(raw);
        const z = celda.t === "n" && !celda.z ? formatoParaExcel(fmt) : null;
        if (z) celda.z = z;
        const f = formulaParaExcel(raw);
        if (f) celda.f = f;
        ws[XLSX.utils.encode_cell({ r, c })] = celda;
        maxR = Math.max(maxR, r);
        maxC = Math.max(maxC, c);
      }
    }
    // Las celdas combinadas: el rango de la hoja tiene que llegar hasta ellas.
    const merges = combinadasParaExcel(h.combinadas);
    for (const m of merges) { maxR = Math.max(maxR, m.e.r); maxC = Math.max(maxC, m.e.c); }
    if (merges.length) ws["!merges"] = merges;
    ws["!ref"] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: maxR, c: maxC } });
    ws["!cols"] = Array.from({ length: maxC + 1 }, (_, c) => ({ wpx: (h.anchos && h.anchos[c]) || ANCHO_COL }));
    let nom = nombreHoja(h.nombre, i);
    while (usados.has(nom.toLowerCase())) nom = nombreHoja(nom.slice(0, 28) + "-" + (i + 1), i);
    usados.add(nom.toLowerCase());
    XLSX.utils.book_append_sheet(wb, ws, nom);
  });
  return wb;
}

